import { variants } from '@aldebaran/common/qVariants';
import type { Variant } from '@aldebaran/common/types/variant';
import { initialQuestionIds } from '@aldebaran/common/constants/kb';
import { isStructuredValue } from '@aldebaran/common/util/structuredValue';
import { ViewableQuestion, ViewableResponse, Value } from '../types/global';
import type { UserResponseStore } from '../types/graph';
import type { QuestionnaireVariant } from '@aldebaran/prisma';

const ONBOARDING_QUESTION_IDS = [
  initialQuestionIds.CONSULTATION_TIME,
  initialQuestionIds.CONSULTATION_DATE,
];

const QUESTIONNAIRE_VARIANT_KEYS = Object.keys(variants);

const isQuestionnaireVariant = (
  value: string,
): value is QuestionnaireVariant => {
  return QUESTIONNAIRE_VARIANT_KEYS.includes(value);
};

// A variant's reportTemplate may be a version-gated resolver function. The
// patient app only surfaces it in the debug panel, and the patient store is
// persisted to localStorage via redux-persist, which JSON-serialises state and
// would drop a function-valued reportTemplate. Collapse any resolver to a
// serializable marker before the config is dispatched.
const toStoredQuestionnaireConfig = (variant: Variant, slug: string) => ({
  ...variant,
  slug,
  reportTemplate:
    typeof variant.reportTemplate === 'function'
      ? ('Variable' as const)
      : variant.reportTemplate,
});

export const findQuestionnaireByVariant = (questionnaireVariant: string) => {
  if (isQuestionnaireVariant(questionnaireVariant)) {
    const variant = variants[questionnaireVariant];
    if (variant) {
      return toStoredQuestionnaireConfig(variant, questionnaireVariant);
    }
  }
  // Fallback to DEFAULT if variant doesn't exist or is invalid
  const defaultVariant = variants['DEFAULT'];
  if (!defaultVariant) {
    throw new Error('DEFAULT questionnaire variant is not configured');
  }
  return toStoredQuestionnaireConfig(defaultVariant, 'DEFAULT');
};

export const getQuestionnaireVariants = () => {
  return Object.entries(variants)
    .filter(
      (entry): entry is [string, NonNullable<(typeof entry)[1]>] =>
        entry[1] != null,
    )
    .map(([key, value]) => ({
      name: value.name,
      slug: key,
      speciality: value.speciality,
    }));
};

export const hasAResponseInputField = (question?: ViewableQuestion | null) => {
  if (!question) return false;
  return question.responses.some(
    (response: ViewableResponse) =>
      response.format === 'DECIMAL' ||
      response.format === 'INTEGER' ||
      response.format === 'NUMBER' ||
      response.format === 'TEXT' ||
      response.format === 'MULTILINE_TEXT' ||
      response.format === 'OTHER_TEXT' ||
      response.format === 'DATE',
  );
};

// "Has the patient answered this response?": a boolean selection, a non-empty
// string (ResponseOtherField), or a number (TIME/DATE/DATETIME fields) all count.
export const responseHasValue = (value: Value | undefined): boolean => {
  if (value === true) return true;
  if (value === false) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return true;
  return isStructuredValue(value);
};

/**
 * True when the patient's current answer is a single, clean button selection —
 * the only kind of answer that auto-navigates to the next page.
 *
 * The decision is driven by the selected response (value === true), not the
 * question's whole response set, so a single-choice question that also offers an
 * "Autre"/free-text/input response still auto-advances on a button tap, while
 * selecting the free-text or input response keeps requiring a manual CTA confirm
 * (a required free-text answer can never be auto-skipped — see ALDO-2376).
 *
 * The button must also be the *only* answered response: typing into a sibling
 * text/number input does not clear a previously tapped button, so a stale button
 * selection can coexist with a filled input (e.g. "Je ne sais pas" next to a
 * number field). That contradictory answer must be confirmed via the CTA, not
 * auto-advanced.
 *
 * Multi-select and AUTOCOMPLETE questions never auto-navigate (AUTOCOMPLETE
 * options also store `true`, but picking one in the dialog must not change page).
 */
export const selectedResponseIsButtonAnswer = (
  question: ViewableQuestion | null | undefined,
  userResponse: UserResponseStore | undefined,
): boolean => {
  if (
    !question ||
    question.multipleChoice ||
    question.format === 'AUTOCOMPLETE'
  ) {
    return false;
  }

  const answered = question.responses.filter((response: ViewableResponse) =>
    responseHasValue(userResponse?.[response.extId]),
  );

  return (
    answered.length === 1 &&
    answered[0].format === 'BUTTON' &&
    userResponse?.[answered[0].extId] === true
  );
};

export const filterOnboardingQuestions = (
  activeQueue: string[],
  currentPage: string,
): string[] => {
  if (currentPage !== 'CONSULTATION') return activeQueue;
  return activeQueue.filter((q) => !ONBOARDING_QUESTION_IDS.includes(q));
};

export const isQuestionnairePageEmpty = (
  displayQueue: string[],
  currentQuestion?: ViewableQuestion,
): boolean =>
  displayQueue.length === 0 ||
  (displayQueue.length === 1 && currentQuestion?.format === 'INFO');
